import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import ProductCard from "../components/ProductCard";
import { MapPin, Star, Wine, Award, Calendar, Quote } from "lucide-react";

const REGION_IMAGES = {
    "Douro": "https://images.pexels.com/photos/5004027/pexels-photo-5004027.jpeg",
    "Alentejo": "https://images.pexels.com/photos/13996604/pexels-photo-13996604.jpeg",
    "Dão": "https://static.prod-images.emergentagent.com/jobs/29081a3e-ac58-482c-aff5-9e6ace12b60e/images/78f8b1c5a7dc4063e49935198bcf10f414cd58f3b196edea1ad97750c90d96ed.png",
    "Vinho Verde": "https://images.pexels.com/photos/5004027/pexels-photo-5004027.jpeg",
    "Beira Alta": "https://static.prod-images.emergentagent.com/jobs/29081a3e-ac58-482c-aff5-9e6ace12b60e/images/b30e383a659f5ab2fd5d39196fa9a13b882ff9621acadcdd84293883937313f7.png",
};

export default function ProducerDetail() {
    const { id } = useParams();
    const [producer, setProducer] = useState(null);

    useEffect(() => {
        api.get(`/producers/${id}`).then((r) => setProducer(r.data));
    }, [id]);

    if (!producer)
        return (
            <>
                <Navbar />
                <div className="pt-32 container-edge label-overline text-wine-dark/60">
                    A carregar…
                </div>
            </>
        );

    const hero = REGION_IMAGES[producer.region] || producer.cover_image;
    const gallery = [hero, ...new Set((producer.products || []).flatMap((p) => p.images || []))].slice(0, 6);

    return (
        <>
            <Navbar />

            {/* HERO */}
            <section className="relative h-[58vh] min-h-[440px] bg-wine-deep overflow-hidden">
                <img
                    src={hero}
                    alt={producer.producer_name}
                    className="absolute inset-0 w-full h-full object-cover kenburns"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-wine-deep/30 via-wine-deep/45 to-wine-deep/95" />
                <div className="relative container-edge h-full flex flex-col justify-end pb-12 text-cream">
                    <div className="flex items-center gap-2 text-gold-soft">
                        <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />
                        <span className="label-overline">{producer.region} · Portugal</span>
                    </div>
                    <h1 className="mt-4 font-heading text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl text-shadow-soft">
                        {producer.producer_name}
                    </h1>
                    <div className="mt-6 flex flex-wrap gap-5 text-cream/85">
                        <Pill icon={Wine} label={`${producer.products_count} referências`} />
                        {producer.reviews_count > 0 && (
                            <Pill icon={Star} label={`${producer.avg_rating} · ${producer.reviews_count} avaliações`} />
                        )}
                        {producer.approved && <Pill icon={Award} label="Verificado Caseirinho" />}
                    </div>
                </div>
            </section>

            {/* STORY — editorial two-col */}
            <section className="bg-cream py-14 md:py-20">
                <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <span className="label-overline text-wine">A história</span>
                        <h2 className="mt-4 font-heading text-3xl md:text-4xl text-wine-dark leading-[1.08]">
                            Tradição, terroir e mãos que conhecem o tempo.
                        </h2>
                        <div className="divider-curl mt-6 text-gold-soft" />
                    </div>
                    <div className="lg:col-span-7">
                        <Quote className="h-8 w-8 text-gold-soft/40" strokeWidth={1} />
                        <p className="mt-4 text-wine-dark/80 text-base md:text-lg leading-[1.7] font-light first-letter:font-heading first-letter:text-5xl first-letter:float-left first-letter:mr-2.5 first-letter:mt-1 first-letter:text-wine first-letter:leading-none">
                            {producer.story}
                        </p>
                    </div>
                </div>
            </section>

            {/* FACTS STRIP */}
            <section className="bg-wine-dark text-cream py-12">
                <div className="container-edge grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
                    <Fact icon={MapPin} label="Região" value={producer.region} />
                    <Fact icon={Wine} label="Catálogo" value={`${producer.products_count} produtos`} />
                    <Fact
                        icon={Star}
                        label="Reputação"
                        value={producer.avg_rating ? `${producer.avg_rating} / 5` : "—"}
                    />
                    <Fact
                        icon={Calendar}
                        label="No Caseirinho desde"
                        value={
                            producer.created_at
                                ? new Date(producer.created_at).toLocaleDateString("pt-PT", {
                                      month: "long",
                                      year: "numeric",
                                  })
                                : "—"
                        }
                    />
                </div>
            </section>

            {/* GALLERY */}
            {gallery.length > 1 && (
                <section className="bg-cream-warm py-14 md:py-16">
                    <div className="container-edge">
                        <div className="flex items-end justify-between mb-8">
                            <div>
                                <span className="label-overline text-wine">Atelier</span>
                                <h2 className="mt-2 font-heading text-3xl text-wine-dark">Fotografias</h2>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                            {gallery.map((src, i) => (
                                <div
                                    key={i}
                                    className="overflow-hidden rounded-md bg-wine/5 aspect-square group"
                                >
                                    <img
                                        src={src}
                                        alt=""
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CATALOG */}
            <section className="bg-cream py-14 md:py-20">
                <div className="container-edge">
                    <div className="flex items-end justify-between mb-8 md:mb-10">
                        <div>
                            <span className="label-overline text-wine">Catálogo</span>
                            <h2 className="mt-2 font-heading text-3xl md:text-4xl text-wine-dark">
                                Tudo de {producer.producer_name}
                            </h2>
                        </div>
                        <span className="label-overline text-wine-dark/60">{producer.products_count} produtos</span>
                    </div>
                    {producer.products?.length === 0 ? (
                        <p className="text-wine-dark/60">Este produtor ainda não tem produtos publicados.</p>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                            {producer.products?.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* REVIEWS */}
            {producer.reviews?.length > 0 && (
                <section className="bg-cream-warm py-14 md:py-20">
                    <div className="container-edge">
                        <div className="flex items-center gap-6 mb-8 md:mb-10">
                            <div>
                                <span className="label-overline text-wine">O que dizem</span>
                                <h2 className="mt-2 font-heading text-3xl md:text-4xl text-wine-dark">
                                    Avaliações dos clientes
                                </h2>
                            </div>
                            <div className="ml-auto flex items-center gap-3">
                                <div className="font-heading text-5xl text-wine-dark leading-none">
                                    {producer.avg_rating}
                                </div>
                                <div>
                                    <div className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star
                                                key={n}
                                                className={`h-3.5 w-3.5 ${
                                                    n <= Math.round(producer.avg_rating)
                                                        ? "fill-gold-soft text-gold-soft"
                                                        : "text-wine-dark/20"
                                                }`}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-xs text-wine-dark/60 mt-1">
                                        {producer.reviews_count} avaliações
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                            {producer.reviews.slice(0, 6).map((r) => (
                                <div
                                    key={r.id}
                                    className="bg-white border border-gold-soft/25 rounded-md p-5 flex flex-col gap-3"
                                    data-testid={`producer-review-${r.id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex gap-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Star
                                                    key={n}
                                                    className={`h-3 w-3 ${
                                                        n <= r.rating
                                                            ? "fill-gold-soft text-gold-soft"
                                                            : "text-wine-dark/20"
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                        <span className="text-[11px] text-wine-dark/50">
                                            {new Date(r.created_at).toLocaleDateString("pt-PT")}
                                        </span>
                                    </div>
                                    <p className="text-sm text-wine-dark/80 leading-relaxed italic flex-1 line-clamp-4">
                                        "{r.comment}"
                                    </p>
                                    <div className="border-t border-wine-dark/5 pt-3 flex items-center justify-between">
                                        <span className="text-sm font-semibold text-wine-dark">
                                            {r.user_name}
                                        </span>
                                        {r.product_name && (
                                            <span className="label-overline text-[10px] text-wine/60">
                                                {r.product_name.length > 20
                                                    ? r.product_name.slice(0, 20) + "…"
                                                    : r.product_name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="bg-wine-dark text-cream py-14 md:py-16">
                <div className="container-edge text-center">
                    <span className="label-overline text-gold-soft">Quer descobrir mais?</span>
                    <h3 className="mt-4 font-heading text-2xl md:text-3xl">
                        Explore outros produtores artesanais portugueses.
                    </h3>
                    <Link to="/produtores" className="btn-gold mt-8 inline-flex" data-testid="back-to-producers">
                        Ver todos os produtores
                    </Link>
                </div>
            </section>

            <Footer />
        </>
    );
}

function Pill({ icon: Icon, label }) {
    return (
        <span className="flex items-center gap-2 text-sm">
            <Icon className="h-4 w-4 text-gold-soft" strokeWidth={1.5} />
            {label}
        </span>
    );
}

function Fact({ icon: Icon, label, value }) {
    return (
        <div className="flex flex-col items-center md:items-start gap-1.5">
            <Icon className="h-4 w-4 text-gold-soft" strokeWidth={1.5} />
            <span className="label-overline text-cream/60">{label}</span>
            <span className="font-heading text-xl md:text-2xl text-cream">{value}</span>
        </div>
    );
}
